/* eslint-disable */
import { useState, useRef, useEffect } from "react";
import CustomBoxModal from "../CustomModalBox";
import Button from "@mui/material/Button";
import { useAppSelector } from "src/utils/hooks/useAppSelector";
import CustomMobileCheckbox from "../CustomMobileCheckbox";

interface CustomOTPMobileModalProps {
  isModal: boolean;
  onClose?: () => void;
  onResendClick: () => void;
  onVerify: (data: { id: string; otp: string }) => void;
  setIsOtpModalOpen: (value: boolean) => any;
  showCancelButton?: boolean;
  modalHeading?: string;
  timerMinutes?: number | null;
  onCancel?: () => void;
  otpLength?: number;
  onMobileNumberChange?: (phone: string) => void;
  onMobileCheckboxChange?: (checked: boolean) => void;
  onMobileSendClick?: (phone: string) => void;
  isSendingMobileOtp?: boolean;
}

function CustomOTPMobileModal({
  isModal,
  onClose,
  onResendClick,
  onVerify,
  setIsOtpModalOpen,
  showCancelButton = true,
  modalHeading,
  timerMinutes,
  onCancel,
  otpLength = 4,
  onMobileNumberChange,
  onMobileCheckboxChange,
  onMobileSendClick,
  isSendingMobileOtp = false
}: CustomOTPMobileModalProps) {
  const otpVerificationDetails = useAppSelector((state) => state.otpVerificationDetails);

  const numInputs = otpLength;

  const [otp, setOtp] = useState<string[]>(Array(numInputs).fill(""));
  const inputRefs = useRef<(HTMLInputElement | null)[]>(Array(numInputs).fill(null));
  const [phoneNumber, setPhoneNumber] = useState("");
  const [isMobileChecked, setIsMobileChecked] = useState(false);

  const [timer, setTimer] = useState(timerMinutes ? timerMinutes * 60 : 0);
  const [isTiming, setIsTiming] = useState(!!timerMinutes);

  useEffect(() => {
    if (!isTiming) return;
    const interval = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          setIsTiming(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [isTiming]);

  const handleResend = () => {
    if (isTiming) return;
    onResendClick();
    if (timerMinutes) {
      setTimer(timerMinutes * 60);
      setIsTiming(true);
    }
  };

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}m ${s < 10 ? "0" : ""}${s}s`;
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const { value } = event.target;

    if (value === "") {
      const updatedOtp = [...otp];
      updatedOtp[index] = "";
      setOtp(updatedOtp);
      // Handle backspace: Remove the input, move to the previous input
      if (index > 0) {
        const prevInput = inputRefs.current[index - 1];
        if (prevInput) {
          prevInput.focus();
        }
      } else if (index === 0) {
        // Handle backspace on the first input by focusing on it without removing its value
        const currentInput = inputRefs.current[index];
        if (currentInput) {
          currentInput.focus();
        }
      }
    } else if (/^\d*$/.test(value) && value.length === 1) {
      // Update the OTP array at the specified index
      const updatedOtp = [...otp];
      updatedOtp[index] = value;
      setOtp(updatedOtp);

      // Move to the next input
      if (index < numInputs - 1) {
        const nextInput = inputRefs.current[index + 1];
        if (nextInput) {
          nextInput.focus();
        }
      }
    }
  };

  return isModal ? (
    <CustomBoxModal onClose={onClose} className="bg-whiteSmoke">
      <div className="w-92.5 text-center mt-5.25 mr-6.25 ml-6.25 text-black font-semibold text-base leading-170">
        {modalHeading}
      </div>
      <div className="mt-4.5 flex justify-center text-base font-semi-bold">
        {otp.map((value, index) => (
          <input
            id={`otp-input-${index}`}
            key={index}
            type="text"
            value={value}
            onChange={(e) => handleChange(e, index)}
            className="w-10 h-10 mr-3 border-2 p-2 rounded focus:outline-none focus:border-hotShot text-center text-base"
            maxLength={1}
            ref={(inputRef) => (inputRefs.current[index] = inputRef)}
          />
        ))}
      </div>

      {timerMinutes !== undefined && timerMinutes !== null && (
        <>
          <div className="w-38.25 h-3.75 text-center mt-6 text-greyText font-medium text-xs opacity-70">
            {isTiming ? `Resend code in ${formatTime(timer)}` : "Didn't receive any code?"}
          </div>

          <div
            onClick={handleResend}
            className={`w-27.25 h-5 mt-3.5 mb-4 text-center font-montserrat font-semibold text-base ${
              isTiming ? "text-greyText" : "text-hotShot cursor-pointer"
            }`}
            style={{ userSelect: "none" }}
          >
            Resend code
          </div>
        </>
      )}

      <div className="px-6 mt-6 mb-6">
        <CustomMobileCheckbox
          phoneValue={phoneNumber}
          onPhoneChange={(phone) => {
            setPhoneNumber(phone);
            if (onMobileNumberChange) {
              onMobileNumberChange(phone);
            }
          }}
          onCheckboxChange={(checked) => {
            setIsMobileChecked(checked);
            if (onMobileCheckboxChange) {
              onMobileCheckboxChange(checked);
            }
          }}
          onSendClick={(phone) => {
            if (onMobileSendClick) {
              onMobileSendClick(phone);
            }
          }}
          checked={isMobileChecked}
          showSendButton
          sendButtonLabel="Send OTP"
          isSending={isSendingMobileOtp}
        />
      </div>

      <div className="flex justify-center mt-4 pb-10 text-center">
        {showCancelButton && (
          <Button
            variant="outlined"
            onClick={() => {
              if (onCancel) {
                onCancel();
              } else {
                setIsOtpModalOpen(false);
              }
            }}
            className="!uppercase !text-black !mr-10 w-37.5 h-12.5 !cursor-pointer rounded-lg"
          >
            CANCEL
          </Button>
        )}
        <Button
          variant="contained"
          disabled={otp.join("").length !== otpLength}
          onClick={() =>
            onVerify({
              id: otpVerificationDetails.otpVerificationId,
              otp: otp.join("")
            })
          }
          className="!uppercase w-37.5 h-12.5 !cursor-pointer rounded-lg"
        >
          VERIFY
        </Button>
      </div>
    </CustomBoxModal>
  ) : (
    <></>
  );
}

export default CustomOTPMobileModal;
